/**
 * W-8IMY Form Hook
 * Custom hook for managing W-8IMY form state using model fields as single source of truth
 */

import { useState, useEffect, useCallback } from "react";
import { W8IMYFormModel } from "../Models/W8IMYModels";
import { W8IMYService } from "../services/W8IMYService";

export interface UseW8IMYFormOptions {
  initialData?: any;
}

export interface UseW8IMYFormReturn {
  model: W8IMYFormModel;
  isLoading: boolean;
  isSaving: boolean;
  errors: Record<string, string>;
  isValid: boolean;
  updateField: (field: string, value: string | boolean) => void;
  updateMultipleFields: (fields: Record<string, string | boolean>) => void;
  validate: () => boolean;
}

export const useW8IMYForm = (options: UseW8IMYFormOptions = {}): UseW8IMYFormReturn => {
  const { initialData } = options;

  // State management using model as single source of truth
  const [model, setModel] = useState<W8IMYFormModel>(() => {
    if (initialData) {
      return W8IMYService.fromApiResponse(initialData);
    }
    return new W8IMYFormModel();
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValid, setIsValid] = useState(false);

  // Update validation state when model changes
  useEffect(() => {
    const validationResult = model.validate();
    setErrors(validationResult.fieldErrors);
    setIsValid(validationResult.isValid);
  }, [model]);

  /**
   * Update a single field in the model
   */
  const updateField = useCallback((field: string, value: string | boolean) => {
    setModel((prevModel) => {
      const newModel = new W8IMYFormModel(prevModel);
      (newModel as any)[field] = value;
      return newModel;
    });
  }, []);

  /**
   * Update multiple fields in the model
   */
  const updateMultipleFields = useCallback((fields: Record<string, string | boolean>) => {
    setModel((prevModel) => {
      const newModel = new W8IMYFormModel(prevModel);
      Object.entries(fields).forEach(([field, value]) => {
        (newModel as any)[field] = value;
      });
      return newModel;
    });
  }, []);

  /**
   * Validate the current model
   */
  const validate = useCallback((): boolean => {
    const validationResult = model.validate();
    setErrors(validationResult.fieldErrors);
    setIsValid(validationResult.isValid);
    return validationResult.isValid;
  }, [model]);

  return {
    model,
    isLoading,
    isSaving,
    errors,
    isValid,
    updateField,
    updateMultipleFields,
    validate,
  };
};
